import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Loader2, AlertCircle, Eye, EyeOff, User, Phone, MessageSquare, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Access request form
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqName, setReqName] = useState('');
  const [reqPhone, setReqPhone] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqAbout, setReqAbout] = useState('');
  const [reqSending, setReqSending] = useState(false);
  const [reqSent, setReqSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-3">
            <img src="/images/viubux-logo-white.png" alt="Viubux" className="h-12" />
          </div>
          <p className="text-slate-400 text-sm">Cotizador de Servicios Audiovisuales</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#0f133a]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-[#aa2aff]" />
            <h2 className="text-lg font-semibold text-white">Acceso Restringido</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  data-testid="input-login-email"
                  className="w-full pl-10 pr-4 py-3 bg-[#080c2d] border border-[#272b96] rounded-xl text-white placeholder:text-slate-600 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  required
                  data-testid="input-login-password"
                  className="w-full pl-10 pr-12 py-3 bg-[#080c2d] border border-[#272b96] rounded-xl text-white placeholder:text-slate-600 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="btn-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="login-error">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="btn-login-submit"
              className="w-full py-3 bg-gradient-to-r from-[#aa2aff] to-[#7012b2] text-white font-semibold rounded-xl hover:from-[#7012b2] hover:to-[#aa2aff] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verificando...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          <p className="text-xs text-slate-600 text-center mt-6">
            Acceso solo para usuarios autorizados
          </p>
        </div>

        {/* Request Access Section */}
        <div className="mt-4 bg-[#0f133a]/60 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowRequestForm(!showRequestForm)}
            className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            data-testid="btn-toggle-request-access"
          >
            <span className="text-sm text-slate-300 font-medium">Solicita acceso</span>
            {showRequestForm ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          
          {showRequestForm && (
            <div className="px-5 pb-5 border-t border-white/5 pt-4">
              {reqSent ? (
                <div className="text-center py-6" data-testid="request-sent-message">
                  <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                    <Send className="w-6 h-6 text-green-400" />
                  </div>
                  <p className="text-white font-semibold text-lg mb-1">¡Solicitud enviada!</p>
                  <p className="text-slate-400 text-sm">Te contactaremos pronto para darte acceso.</p>
                </div>
              ) : (
              <div className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text" value={reqName} onChange={(e) => setReqName(e.target.value)}
                  placeholder="Nombre completo"
                  data-testid="input-req-name"
                  className="w-full pl-10 pr-4 py-2.5 bg-[#080c2d] border border-[#272b96] rounded-xl text-white text-sm placeholder:text-slate-600 focus:border-[#aa2aff] outline-none"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="tel" value={reqPhone} onChange={(e) => setReqPhone(e.target.value)}
                  placeholder="Teléfono"
                  data-testid="input-req-phone"
                  className="w-full pl-10 pr-4 py-2.5 bg-[#080c2d] border border-[#272b96] rounded-xl text-white text-sm placeholder:text-slate-600 focus:border-[#aa2aff] outline-none"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="input-req-email"
                  className="w-full pl-10 pr-4 py-2.5 bg-[#080c2d] border border-[#272b96] rounded-xl text-white text-sm placeholder:text-slate-600 focus:border-[#aa2aff] outline-none"
                />
              </div>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <textarea
                  value={reqAbout} onChange={(e) => setReqAbout(e.target.value)}
                  placeholder="Platícanos más sobre ti"
                  rows={3}
                  data-testid="input-req-about"
                  className="w-full pl-10 pr-4 py-2.5 bg-[#080c2d] border border-[#272b96] rounded-xl text-white text-sm placeholder:text-slate-600 focus:border-[#aa2aff] outline-none resize-none"
                />
              </div>
              <button
                onClick={async () => {
                  if (!reqName || !reqEmail) { toast.error('Nombre y email son requeridos'); return; }
                  setReqSending(true);
                  try {
                    await axios.post(`${API}/auth/request-access`, {
                      name: reqName, phone: reqPhone, email: reqEmail, about: reqAbout
                    });
                    setReqSent(true);
                    setReqName(''); setReqPhone(''); setReqEmail(''); setReqAbout('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
                  } finally { setReqSending(false); }
                }}
                disabled={reqSending}
                data-testid="btn-send-request"
                className="w-full py-2.5 bg-gradient-to-r from-[#272b96] to-[#003fdd] text-white text-sm font-medium rounded-xl hover:from-[#003fdd] hover:to-[#272b96] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {reqSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {reqSending ? 'Enviando...' : 'Enviar Solicitud'}
              </button>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
